import { useState } from "react";
import { Link } from "react-router-dom";
import toast from "react-hot-toast";
import { Download, FileStack } from "lucide-react";
import { useAllDocuments, downloadDocumentBytesGlobal, formatFileSize, DOCUMENT_CATEGORY_LABEL, DocumentCategory } from "@/api/documents";
import { displayName } from "@/api/clients";
import { getApiErrorMessage } from "@/api/client";
import { useAuthStore } from "@/stores/authStore";

/**
 * Módulo "Documentos" (continuación posterior al cierre de la Fase 26):
 * los archivos de TODOS los clientes en un solo lugar, en vez de tener que
 * entrar a cada ficha de cliente por separado. Es de solo lectura +
 * descarga — subir/editar/versionar sigue viviendo en el expediente del
 * cliente (`/clients/:id`), donde categoría/OCR tienen contexto.
 */
export default function DocumentsPage() {
  const [search, setSearch] = useState("");
  const [category, setCategory] = useState<DocumentCategory | "">("");
  const [page, setPage] = useState(1);
  const hasPermission = useAuthStore((s) => s.hasPermission);

  const { data, isLoading } = useAllDocuments({
    search: search || undefined,
    category: category || undefined,
    page,
    pageSize: 20,
  });
  const documents = data?.data ?? [];
  const meta = data?.meta;

  const handleDownload = async (documentId: string) => {
    try {
      await downloadDocumentBytesGlobal(documentId);
    } catch (error) {
      toast.error(getApiErrorMessage(error));
    }
  };

  return (
    <div className="space-y-4">
      <div>
        <h2 className="text-lg font-semibold text-gray-900">Documentos</h2>
        <p className="text-sm text-gray-500">Archivos de todos los clientes de la organización, en un solo lugar.</p>
      </div>

      <div className="flex flex-wrap gap-2">
        <input className="input-field w-64" placeholder="Buscar por nombre de archivo..." value={search} onChange={(e) => setSearch(e.target.value)} />
        <select className="input-field w-auto" value={category} onChange={(e) => setCategory(e.target.value as DocumentCategory | "")}>
          <option value="">Todas las categorías</option>
          {Object.entries(DOCUMENT_CATEGORY_LABEL).map(([value, label]) => (
            <option key={value} value={value}>
              {label}
            </option>
          ))}
        </select>
      </div>

      <div className="card overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 text-left text-xs font-medium uppercase tracking-wide text-gray-400">
            <tr>
              <th className="px-4 py-2.5">Archivo</th>
              <th className="px-4 py-2.5">Cliente</th>
              <th className="px-4 py-2.5">Categoría</th>
              <th className="px-4 py-2.5">Tamaño</th>
              <th className="px-4 py-2.5">Subido por</th>
              <th className="px-4 py-2.5" />
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {isLoading ? (
              <tr>
                <td colSpan={6} className="px-4 py-6 text-center text-gray-400">
                  Cargando...
                </td>
              </tr>
            ) : documents.length === 0 ? (
              <tr>
                <td colSpan={6} className="px-4 py-10 text-center text-gray-400">
                  <FileStack className="mx-auto mb-2" size={28} />
                  No hay documentos que coincidan con los filtros.
                </td>
              </tr>
            ) : (
              documents.map((doc) => (
                <tr key={doc.id}>
                  <td className="px-4 py-2.5 font-medium text-gray-900">{doc.originalName}</td>
                  <td className="px-4 py-2.5">
                    {doc.client ? (
                      <Link to={`/clients/${doc.client.id}`} className="text-brand-600 hover:underline">
                        {displayName(doc.client as never)}
                      </Link>
                    ) : (
                      "—"
                    )}
                  </td>
                  <td className="px-4 py-2.5 text-gray-500">{DOCUMENT_CATEGORY_LABEL[doc.category]}</td>
                  <td className="px-4 py-2.5 text-gray-500">{formatFileSize(doc.sizeBytes)}</td>
                  <td className="px-4 py-2.5 text-gray-500">{doc.uploadedBy?.fullName ?? "—"}</td>
                  <td className="px-4 py-2.5 text-right">
                    {hasPermission("documents.download") && (
                      <button type="button" className="rounded-md p-1.5 text-gray-400 hover:bg-gray-50 hover:text-gray-700" onClick={() => handleDownload(doc.id)} title="Descargar">
                        <Download size={16} />
                      </button>
                    )}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {meta && meta.totalPages && meta.totalPages > 1 && (
        <div className="flex items-center justify-between text-sm text-gray-500">
          <span>
            Página {meta.page} de {meta.totalPages} · {meta.total} documentos
          </span>
          <div className="flex gap-2">
            <button type="button" className="btn-secondary" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>
              Anterior
            </button>
            <button type="button" className="btn-secondary" disabled={!!meta.totalPages && page >= meta.totalPages} onClick={() => setPage((p) => p + 1)}>
              Siguiente
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
